import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useChannelStore } from '../../../stores/channelStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const DEMO_HOOKS = [
  { id: 'wh-1', name: 'GitHub CI', channelId: 'ch-1', avatar_url: null, createdAt: new Date(Date.now() - 86400000 * 7).toISOString() },
  { id: 'wh-2', name: 'Deploy Bot', channelId: 'ch-1', avatar_url: null, createdAt: new Date(Date.now() - 86400000 * 2).toISOString() },
];

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

export function WebhooksModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);
  const channels = useChannelStore(s => s.channels);

  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [view, setView]         = useState('list');   // list | create
  const [form, setForm]         = useState({ name: '', channelId: '' });
  const [saving, setSaving]     = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied]     = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'webhooks');

  const serverId  = data?.serverId || currentServerId;
  const isDemo    = serverId?.startsWith('demo-');
  const textChans = (channels || []).filter(c => c.type !== 'voice');

  useEffect(() => {
    if (!isOpen || type !== 'webhooks') return;
    setView('list'); setNewToken(null); setForm({ name: '', channelId: textChans[0]?.id || '' });
    if (isDemo) { setWebhooks(DEMO_HOOKS); return; }
    setLoading(true);
    fetch(`/api/servers/${serverId}/webhooks`, { headers: AUTH() })
      .then(r => r.json()).then(d => { setWebhooks(d.webhooks || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [isOpen, type]);

  if (!isOpen || type !== 'webhooks') return null;

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    if (isDemo) {
      const hook = { id: `wh-${Date.now()}`, ...form, createdAt: new Date().toISOString() };
      const fakeToken = 'demo_' + Math.random().toString(36).slice(2, 18);
      setWebhooks(w => [...w, hook]);
      setNewToken(`${window.location.origin}/api/webhooks/incoming/${fakeToken}`);
      setView('list');
    } else {
      const r = await fetch(`/api/servers/${serverId}/webhooks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...AUTH() },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (d.success) {
        setWebhooks(w => [...w, d.webhook]);
        setNewToken(`${window.location.origin}/api/webhooks/incoming/${d.webhook.token}`);
        setView('list');
      }
    }
    setSaving(false); setForm({ name: '', channelId: textChans[0]?.id || '' });
  };

  const handleDelete = async (id) => {
    if (confirmDelete !== id) { setConfirmDelete(id); return; }
    if (!isDemo) await fetch(`/api/servers/${serverId}/webhooks/${id}`, { method: 'DELETE', headers: AUTH() });
    setWebhooks(w => w.filter(h => h.id !== id));
    setConfirmDelete(null);
  };

  const handleCopy = (text, key) => {
    copyToClipboard(text); setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const chanName = (id) => (channels || []).find(c => c.id === id)?.name || id || '—';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label="Webhooks" onClick={e => e.stopPropagation()}
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, width: 580, maxWidth: '95vw', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>🔗 Webhooks</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Send data into channels from external services</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {view === 'list' && (
              <button onClick={() => setView('create')}
                style={{ padding: '6px 14px', borderRadius: 7, background: '#6366f1', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                + New Webhook
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* New token banner */}
          {newToken && (
            <div style={{ padding: '12px 14px', borderRadius: 8, background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#34d399', marginBottom: 6 }}>✓ Webhook created — copy the URL now, it won't be shown again.</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={{ flex: 1, fontSize: 11, color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.3)', padding: '6px 10px', borderRadius: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{newToken}</code>
                <button onClick={() => handleCopy(newToken, 'new')}
                  style={{ padding: '6px 12px', borderRadius: 6, background: copied === 'new' ? 'rgba(52,211,153,0.2)' : 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: copied === 'new' ? '#34d399' : 'rgba(255,255,255,0.6)', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                  {copied === 'new' ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {/* Create form */}
          {view === 'create' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>Webhook Name</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. GitHub CI"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 7 }}>Post to Channel</label>
                <select value={form.channelId} onChange={e => setForm(f => ({ ...f, channelId: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                  {textChans.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                  {textChans.length === 0 && <option value="">No channels</option>}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
                <button onClick={() => setView('list')} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleCreate} disabled={!form.name.trim() || saving}
                  style={{ padding: '8px 20px', borderRadius: 7, background: form.name.trim() ? '#6366f1' : 'rgba(99,102,241,0.4)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: form.name.trim() ? 'pointer' : 'default' }}>
                  {saving ? 'Creating…' : 'Create Webhook'}
                </button>
              </div>
            </div>
          )}

          {/* List */}
          {view === 'list' && (
            loading ? (
              <div style={{ textAlign: 'center', paddingTop: 40, color: 'rgba(255,255,255,0.3)' }}>Loading…</div>
            ) : webhooks.length === 0 ? (
              <div style={{ textAlign: 'center', paddingTop: 50, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>🔗</div>
                No webhooks yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {webhooks.map(hook => (
                  <div key={hook.id} style={{ padding: '12px 14px', borderRadius: 9, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🔗</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{hook.name}</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>→ #{chanName(hook.channelId)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => handleDelete(hook.id)}
                        style={{ padding: '5px 11px', borderRadius: 6, background: confirmDelete === hook.id ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.08)', border: `1px solid rgba(239,68,68,${confirmDelete === hook.id ? '0.6' : '0.25'})`, color: '#f87171', fontSize: 12, cursor: 'pointer' }}>
                        {confirmDelete === hook.id ? 'Confirm' : '🗑 Delete'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
